// mostly copied from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/GLTFLoader.js
import ExternalLoader, {
  GeometryRawData,
  LoaderDataType,
  Material,
  ObjectData
} from "_src/engine/loaders/external/external_loader";
import fakeBlob from "_src/engine/fake_blob";
import fetch from "_fetch";
import {decodeText} from "_src/utils";

const TrianglesDrawMode = 0;
const TriangleStripDrawMode = 1;
const TriangleFanDrawMode = 2;

const BINARY_EXTENSION_HEADER_MAGIC = "glTF";
const BINARY_EXTENSION_HEADER_LENGTH = 12;
const BINARY_EXTENSION_CHUNK_TYPES = { JSON: 0x4E4F534A, BIN: 0x004E4942 };

const EXTENSIONS = {
  KHR_BINARY_GLTF: "KHR_binary_glTF",
  KHR_DRACO_MESH_COMPRESSION: "KHR_draco_mesh_compression",
  KHR_LIGHTS_PUNCTUAL: "KHR_lights_punctual",
  KHR_MATERIALS_CLEARCOAT: "KHR_materials_clearcoat",
  KHR_MATERIALS_IOR: "KHR_materials_ior",
  KHR_MATERIALS_SHEEN: "KHR_materials_sheen",
  KHR_MATERIALS_SPECULAR: "KHR_materials_specular",
  KHR_MATERIALS_TRANSMISSION: "KHR_materials_transmission",
  KHR_MATERIALS_IRIDESCENCE: "KHR_materials_iridescence",
  KHR_MATERIALS_UNLIT: "KHR_materials_unlit",
  KHR_MATERIALS_VOLUME: "KHR_materials_volume",
  KHR_TEXTURE_BASISU: "KHR_texture_basisu",
  KHR_TEXTURE_TRANSFORM: "KHR_texture_transform",
  KHR_MESH_QUANTIZATION: "KHR_mesh_quantization",
  KHR_MATERIALS_EMISSIVE_STRENGTH: "KHR_materials_emissive_strength",
  EXT_TEXTURE_WEBP: "EXT_texture_webp",
  EXT_MESHOPT_COMPRESSION: "EXT_meshopt_compression",
  EXT_MESH_GPU_INSTANCING: "EXT_mesh_gpu_instancing"
};

const WEBGL_TYPE_SIZES = {
  "SCALAR": 1,
  "VEC2": 2,
  "VEC3": 3,
  "VEC4": 4,
  "MAT2": 4,
  "MAT3": 9,
  "MAT4": 16
};

const WEBGL_COMPONENT_TYPES = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array
};

const ATTRIBUTES = {
  POSITION: "positions",
  NORMAL: "normals",
  TEXCOORD_0: "uvs",
  COLOR_0: "colors",
};

const WEBGL_CONSTANTS = {
  FLOAT: 5126,
  //FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123
};

export default class GltfLoader extends ExternalLoader {
  constructor(isBinary) {
    super();
    this._dataType = isBinary ? LoaderDataType.BINARY : LoaderDataType.TEXT;
  }

  async _parse(data, progress = null) {
    let json;
    const extensions = {};

    if ( this._dataType === LoaderDataType.BINARY ) {

      const magic = decodeText( new Uint8Array( data, 0, 4 ) );

      if ( magic === BINARY_EXTENSION_HEADER_MAGIC ) {

        extensions[ EXTENSIONS.KHR_BINARY_GLTF ] = new GLTFBinaryExtension( data );

        json = JSON.parse( extensions[ EXTENSIONS.KHR_BINARY_GLTF ].content );

      } else {

        json = JSON.parse( decodeText( data ) );

      }

    } else {

      json = JSON.parse( data );

    }

    if ( json.asset === undefined || json.asset.version[ 0 ] < 2 ) {
      throw new Error( "ExternalLoader.GLTFLoader: Unsupported asset. glTF versions < 2.0 are supported." );
    }

    const parser = new GLTFParser( json, this._basePath );
    parser.setExtensions( extensions );

    const treeData = await parser.parse();

    const objectsData = [];
    parseTreeData(treeData.scenes, objectsData);

    if (progress) progress.emit(progress.LOADING, 1);

    return objectsData;
  }
}

function parseTreeData(object, objectsData) {
  if (Array.isArray(object)) {
    for (const elem of object) {
      parseTreeData(elem, objectsData);
    }
  } else {
    const material = new Material();
    material.flipTexture = false;
    if (object.texture && object.texture.map) material.baseTexture = object.texture.map;
    if (object.texture && object.texture.metalnessRoughnessMap) material.metallicRoughnessTexture = object.texture.metalnessRoughnessMap;

    const objectData = new ObjectData(object.geometry, material);
    objectsData.push(objectData);
  }
}

class GLTFParser {

  constructor( json = {}, basePath ) {

    this.basePath = basePath;
    this.json = json;
    this.extensions = {};
    this.plugins = {};

    // loader object cache
    this.cache = new GLTFRegistry();

    // associations between Three.js objects and glTF elements
    this.associations = new Map();

    // BufferGeometry caching
    this.primitiveCache = {};

    // Object3D instance caches
    this.meshCache = { refs: {}, uses: {} };

    this.sourceCache = {};
    this.textureCache = {};
  }

  setExtensions( extensions ) {
    this.extensions = extensions;
  }

  async parse() {

    const parser = this;
    const json = this.json;

    // Clear the loader cache
    this.cache.removeAll();

    // Mark the special nodes/meshes in json for efficient parse
    this._invokeAll( function ( ext ) {

      return ext._markDefs && ext._markDefs();

    } );

    const dependencies = await Promise.all( [
      parser.getDependencies( "scene" ),
    ] );

    const result = {
      scene: dependencies[ 0 ][ json.scene || 0 ],
      scenes: dependencies[ 0 ],
      asset: json.asset,
      parser: parser,
      userData: {}
    };

    return result;
  }

  /**
   * Marks the special nodes/meshes in json for efficient parse.
   */
  _markDefs() {

    const nodeDefs = this.json.nodes || [];

    // Iterate over all nodes, marking references to shared resources,
    // as well as skeleton joints.
    for ( let nodeIndex = 0, nodeLength = nodeDefs.length; nodeIndex < nodeLength; nodeIndex ++ ) {

      const nodeDef = nodeDefs[ nodeIndex ];

      if ( nodeDef.mesh !== undefined ) {
        this._addNodeRef( this.meshCache, nodeDef.mesh );
      }

    }

  }

  /**
   * Counts references to shared node / Object3D resources. These resources
   * can be reused, or "instantiated", at multiple nodes in the scene
   * hierarchy. Mesh, Camera, and Light instances are instantiated and must
   * be marked. Non-scenegraph resources (like Materials, Geometries, and
   * Textures) can be reused directly and are not marked here.
   *
   * Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
   */
  _addNodeRef( cache, index ) {

    if ( index === undefined ) return;

    if ( cache.refs[ index ] === undefined ) {

      cache.refs[ index ] = cache.uses[ index ] = 0;

    }

    cache.refs[ index ] ++;

  }

  /** Returns a reference to a shared resource, cloning it if necessary. */
  _getNodeRef( cache, index, object ) {

    if ( cache.refs[ index ] <= 1 ) return object;

    const ref = object.slice();

    // Propagates mappings to the cloned object, prevents mappings on the
    // original object from being lost.
    const updateMappings = ( original, clone ) => {
      if (!Array.isArray(original)) {
        const mappings = this.associations.get( original );
        if ( mappings != null ) {
          this.associations.set( clone, mappings );
        }
      } else {
        for (const [i, child] of original.entries()) {
          updateMappings(child, clone[i]);
        }
      }
    };

    updateMappings( object, ref );

    return ref;

  }

  _invokeOne( func ) {

    const extensions = Object.values( this.plugins );
    extensions.push( this );

    for ( let i = 0; i < extensions.length; i ++ ) {

      const result = func( extensions[ i ] );

      if ( result ) return result;

    }

    return null;

  }

  _invokeAll( func ) {

    const extensions = Object.values( this.plugins );
    extensions.unshift( this );

    const pending = [];

    for ( let i = 0; i < extensions.length; i ++ ) {

      const result = func( extensions[ i ] );

      if ( result ) pending.push( result );

    }

    return pending;

  }

  getDependency( type, index ) {

    const cacheKey = type + ":" + index;
    let dependency = this.cache.get( cacheKey );

    if ( ! dependency ) {

      switch ( type ) {

        case "scene":
          dependency = this.loadScene( index );
          break;

        case "node":
          dependency = this._invokeOne( function ( ext ) {

            return ext.loadNode && ext.loadNode( index );

          } );
          break;

        case "mesh":
          dependency = this._invokeOne( function ( ext ) {

            return ext.loadMesh && ext.loadMesh( index );

          } );
          break;

        case "accessor":
          dependency = this.loadAccessor( index );
          break;

        case "bufferView":
          dependency = this._invokeOne( function ( ext ) {

            return ext.loadBufferView && ext.loadBufferView( index );

          } );
          break;

        case "buffer":
          dependency = this.loadBuffer( index );
          break;

        case "material":
          dependency = this._invokeOne( function ( ext ) {

            return ext.loadMaterial && ext.loadMaterial( index );

          } );
          break;

        case "texture":
          dependency = this._invokeOne( function ( ext ) {

            return ext.loadTexture && ext.loadTexture( index );

          } );
          break;

        case "skin":
        case "animation":
        case "camera":
          //not implemented
          break;

        default:
          dependency = this._invokeOne( function ( ext ) {

            return ext != this && ext.getDependency && ext.getDependency( type, index );

          } );

          if ( ! dependency ) {

            throw new Error( "ExternalLoader.GLTFLoader: Unknown dependency type: " + type );

          }

          break;

      }

      this.cache.add( cacheKey, dependency );

    }

    return dependency;

  }

  getDependencies( type ) {

    let dependencies = this.cache.get( type );

    if ( ! dependencies ) {

      const parser = this;
      const defs = this.json[ type + ( type === "mesh" ? "es" : "s" ) ] || [];

      dependencies = Promise.all( defs.map( function ( def, index ) {

        return parser.getDependency( type, index );

      } ) );

      this.cache.add( type, dependencies );

    }

    return dependencies;

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   */
  async loadBuffer( bufferIndex ) {

    const bufferDef = this.json.buffers[ bufferIndex ];

    if ( bufferDef.type && bufferDef.type !== "arraybuffer" ) {
      throw new Error( "ExternalLoader.GLTFLoader: " + bufferDef.type + " buffer type is not supported." );
    }

    // If present, GLB container is required to be the first buffer.
    if ( bufferDef.uri === undefined && bufferIndex === 0 ) {
      return this.extensions[ EXTENSIONS.KHR_BINARY_GLTF ].body;
    }

    const fullPath = this.resolvePath(bufferDef.uri);
    const response = await fetch(fullPath);
    if (!response.ok) {
      throw new Error("ExternalLoader.GLTFLoader: buffer was not loaded from " + fullPath);
    }

    const data = await response.arrayBuffer();
    return data;
  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
   */
  loadBufferView( bufferViewIndex ) {

    const bufferViewDef = this.json.bufferViews[ bufferViewIndex ];

    return this.getDependency( "buffer", bufferViewDef.buffer ).then( function ( buffer ) {

      const byteLength = bufferViewDef.byteLength || 0;
      const byteOffset = bufferViewDef.byteOffset || 0;
      return buffer.slice( byteOffset, byteOffset + byteLength );

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
   */
  loadAccessor( accessorIndex ) {
    const json = this.json;

    const accessorDef = this.json.accessors[ accessorIndex ];

    if ( accessorDef.bufferView === undefined && accessorDef.sparse === undefined ) {

      const itemSize = WEBGL_TYPE_SIZES[ accessorDef.type ];
      const TypedArray = WEBGL_COMPONENT_TYPES[ accessorDef.componentType ];

      const array = new TypedArray( accessorDef.count * itemSize );
      return Promise.resolve( array );

    }

    const pendingBufferViews = [];

    if ( accessorDef.bufferView !== undefined ) {

      pendingBufferViews.push( this.getDependency( "bufferView", accessorDef.bufferView ) );

    } else {

      pendingBufferViews.push( null );

    }

    if ( accessorDef.sparse !== undefined ) {

      pendingBufferViews.push( this.getDependency( "bufferView", accessorDef.sparse.indices.bufferView ) );
      pendingBufferViews.push( this.getDependency( "bufferView", accessorDef.sparse.values.bufferView ) );

    }

    return Promise.all( pendingBufferViews ).then( function ( bufferViews ) {

      const bufferView = bufferViews[ 0 ];

      const itemSize = WEBGL_TYPE_SIZES[ accessorDef.type ];
      const TypedArray = WEBGL_COMPONENT_TYPES[ accessorDef.componentType ];

      // For VEC3: itemSize is 3, elementBytes is 4, itemBytes is 12.
      const elementBytes = TypedArray.BYTES_PER_ELEMENT;
      const itemBytes = elementBytes * itemSize;
      const byteOffset = accessorDef.byteOffset || 0;
      const byteStride = accessorDef.bufferView !== undefined ? json.bufferViews[ accessorDef.bufferView ].byteStride : undefined;
      let array;

      // The buffer is not interleaved if the stride is the item size in bytes.
      if ( byteStride && byteStride !== itemBytes ) {
        throw new Error( "ExternalLoader.GLTFLoader: interleaved buffer is not supported" );
      } else {

        if ( bufferView === null ) {

          array = new TypedArray( accessorDef.count * itemSize );

        } else {

          array = new TypedArray( bufferView, byteOffset, accessorDef.count * itemSize );

        }

      }

      // https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#sparse-accessors
      if ( accessorDef.sparse !== undefined ) {

        const itemSizeIndices = WEBGL_TYPE_SIZES.SCALAR;
        const TypedArrayIndices = WEBGL_COMPONENT_TYPES[ accessorDef.sparse.indices.componentType ];

        const byteOffsetIndices = accessorDef.sparse.indices.byteOffset || 0;
        const byteOffsetValues = accessorDef.sparse.values.byteOffset || 0;

        const sparseIndices = new TypedArrayIndices( bufferViews[ 1 ], byteOffsetIndices, accessorDef.sparse.count * itemSizeIndices );
        const sparseValues = new TypedArray( bufferViews[ 2 ], byteOffsetValues, accessorDef.sparse.count * itemSize );

        if ( bufferView !== null ) {

          // Avoid modifying the original ArrayBuffer, if the bufferView wasn't initialized with zeroes.
          array = array.slice();

        }

        for ( let i = 0, il = sparseIndices.length; i < il; i ++ ) {

          const index = sparseIndices[ i ];

          array[index*itemSize] = sparseValues[ i * itemSize ];
          if ( itemSize >= 2 ) array[index*itemSize + 1] = sparseValues[ i * itemSize + 1 ];
          if ( itemSize >= 3 ) array[index*itemSize + 2] = sparseValues[ i * itemSize + 2 ];
          if ( itemSize >= 4 ) array[index*itemSize + 3] = sparseValues[ i * itemSize + 3 ];
          if ( itemSize >= 5 ) throw new Error( "ExternalLoader.GLTFLoader: Unsupported itemSize in sparse BufferAttribute." );

        }

      }

      return array;

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
   */
  loadTexture( textureIndex ) {

    const json = this.json;
    const textureDef = json.textures[ textureIndex ];
    const sourceIndex = textureDef.source;

    return this.loadTextureImage( textureIndex, sourceIndex );

  }

  loadTextureImage( textureIndex, sourceIndex ) {

    const parser = this;
    const json = this.json;

    const textureDef = json.textures[ textureIndex ];
    const sourceDef = json.images[ sourceIndex ];

    const cacheKey = ( sourceDef.uri || sourceDef.bufferView ) + ":" + textureDef.sampler;

    if ( this.textureCache[ cacheKey ] ) {

      // See https://github.com/mrdoob/three.js/issues/21559.
      return this.textureCache[ cacheKey ];

    }

    const promise = this.loadImageSource( sourceIndex ).then( function ( texture ) {

      parser.associations.set( texture, { textures: textureIndex } );

      return texture;

    } ).catch( function () {

      return null;

    } );

    this.textureCache[ cacheKey ] = promise;

    return promise;

  }

  loadImageSource( sourceIndex ) {

    const parser = this;
    const json = this.json;

    if ( this.sourceCache[ sourceIndex ] !== undefined ) {

      return this.sourceCache[ sourceIndex ].then( ( texture ) => texture.clone() );

    }

    const sourceDef = json.images[ sourceIndex ];

    let sourceURI;

    if ( sourceDef.bufferView !== undefined ) {

      sourceURI = parser.getDependency( "bufferView", sourceDef.bufferView ).then( function ( bufferView ) {

        if ( typeof Blob !== "undefined" ) {
          const blob = new Blob( [ bufferView ], { type: sourceDef.mimeType } );
          sourceURI = URL.createObjectURL( blob );
        } else {
          sourceURI = fakeBlob.registerData(bufferView);
        }

        return sourceURI;

      } );

    } else if ( sourceDef.uri === undefined ) {

      throw new Error( "ExternalLoader.GLTFLoader: Image " + sourceIndex + " is missing URI and bufferView" );

    } else {
      sourceURI = this.resolvePath(sourceDef.uri || "");
    }

    const promise = Promise.resolve(sourceURI);
    this.sourceCache[ sourceIndex ] = promise;
    return promise;
  }

  resolvePath(uri) {
    if ( /^data:.*,.*$/i.test( uri ) ) return uri;
    else return this.basePath + uri;
  }

  assignTexture( materialParams, mapName, mapDef ) {

    return this.getDependency( "texture", mapDef.index ).then( function ( texture ) {

      if ( ! texture ) return null;

      materialParams[ mapName ] = texture;

      return texture;

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
   */
  loadMaterial( materialIndex ) {

    const parser = this;
    const json = this.json;
    const materialDef = json.materials[ materialIndex ];

    const materialParams = {};
    const materialExtensions = materialDef.extensions || {};

    const pending = [];

    if ( materialExtensions[ EXTENSIONS.KHR_MATERIALS_UNLIT ] ) {

      // not implemented

    } else {

      // Specification:
      // https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#metallic-roughness-material

      const metallicRoughness = materialDef.pbrMetallicRoughness || {};
      if ( metallicRoughness.baseColorTexture !== undefined ) {
        pending.push( parser.assignTexture( materialParams, "map", metallicRoughness.baseColorTexture ) );
      }

      if ( metallicRoughness.metallicRoughnessTexture !== undefined ) {
        pending.push( parser.assignTexture( materialParams, "metalnessRoughnessMap", metallicRoughness.metallicRoughnessTexture ) );
      }
    }

    return Promise.all( pending ).then( function () {

      const material = materialParams;

      parser.associations.set( material, { materials: materialIndex } );

      return material;

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
   */
  loadGeometries( primitives ) {

    const parser = this;
    const cache = this.primitiveCache;

    const pending = [];

    for ( let i = 0, il = primitives.length; i < il; i ++ ) {

      const primitive = primitives[ i ];
      const cacheKey = createPrimitiveKey( primitive );

      // See if we've already created this geometry
      const cached = cache[ cacheKey ];

      if ( cached ) {

        // Use the cached geometry if it exists
        pending.push( cached.promise );

      } else {

        let geometryPromise;

        if ( primitive.extensions && primitive.extensions[ EXTENSIONS.KHR_DRACO_MESH_COMPRESSION ] ) {
          throw new Error( "ExternalLoader.GLTFLoader: Draco compression is not supported." );
        } else {

          // Otherwise create a new geometry
          geometryPromise = addPrimitiveAttributes( primitive, parser );

        }

        // Cache this geometry
        cache[ cacheKey ] = { primitive: primitive, promise: geometryPromise };

        pending.push( geometryPromise );

      }

    }

    return Promise.all( pending );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
   */
  loadMesh( meshIndex ) {

    const parser = this;
    const json = this.json;

    const meshDef = json.meshes[ meshIndex ];
    const primitives = meshDef.primitives;

    const pending = [];

    for ( let i = 0, il = primitives.length; i < il; i ++ ) {

      const material = primitives[ i ].material === undefined
        ? null
        : this.getDependency( "material", primitives[ i ].material );

      pending.push( material );

    }

    pending.push( parser.loadGeometries( primitives ) );

    return Promise.all( pending ).then( function ( results ) {

      const materials = results.slice( 0, results.length - 1 );
      const geometries = results[ results.length - 1 ];

      const meshes = [];

      for ( let i = 0, il = geometries.length; i < il; i ++ ) {

        const geometry = geometries[ i ];
        const primitive = primitives[ i ];

        // 1. create Mesh

        let mesh;

        const material = materials[ i ];

        if ( primitive.mode === WEBGL_CONSTANTS.TRIANGLES ||
          primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP ||
          primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN ||
          primitive.mode === undefined ) {

          // .isSkinnedMesh isn't in glTF spec. See ._markDefs()
          if (meshDef.isSkinnedMesh) {
            //not supported
            continue;
          }
          mesh = {
            geometry: geometry,
            texture: material,
          };

          if ( primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP ) {

            mesh.geometry = toTrianglesDrawMode( mesh.geometry, TriangleStripDrawMode );

          } else if ( primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN ) {

            mesh.geometry = toTrianglesDrawMode( mesh.geometry, TriangleFanDrawMode );

          }

        } else if ( primitive.mode === WEBGL_CONSTANTS.LINES || primitive.mode === WEBGL_CONSTANTS.LINE_STRIP || primitive.mode === WEBGL_CONSTANTS.LINE_LOOP || primitive.mode === WEBGL_CONSTANTS.POINTS) {
          //not supported
          continue;
        } else {
          throw new Error( "ExternalLoader.GLTFLoader: Primitive mode unsupported: " + primitive.mode );
        }

        meshes.push( mesh );

      }

      for ( let i = 0, il = meshes.length; i < il; i ++ ) {

        parser.associations.set( meshes[ i ], {
          meshes: meshIndex,
          primitives: i
        } );

      }

      parser.associations.set( meshes, { meshes: meshIndex } );

      return meshes;

    } );

  }

  createNodeMesh( nodeIndex ) {

    const json = this.json;
    const parser = this;
    const nodeDef = json.nodes[ nodeIndex ];

    if ( nodeDef.mesh === undefined ) return null;

    return parser.getDependency( "mesh", nodeDef.mesh ).then( function ( mesh ) {

      const node = parser._getNodeRef( parser.meshCache, nodeDef.mesh, mesh );
      return node;

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
   */
  loadNode( nodeIndex ) {

    const json = this.json;
    const parser = this;

    const nodeDef = json.nodes[ nodeIndex ];

    return ( function () {

      const objectPending = [];

      const meshPromise = parser._invokeOne( function ( ext ) {

        return ext.createNodeMesh && ext.createNodeMesh( nodeIndex );

      } );

      if ( meshPromise ) {

        objectPending.push( meshPromise );

      }

      const childPending = [];
      const childrenDef = nodeDef.children || [];

      for ( let i = 0, il = childrenDef.length; i < il; i ++ ) {

        childPending.push( parser.getDependency( "node", childrenDef[ i ] ) );

      }

      return Promise.all( [
        Promise.all( objectPending ),
        Promise.all( childPending )
      ] );

    }() ).then( function ( results ) {

      const objects = results[ 0 ];
      const children = results[ 1 ];

      let node = objects;
      if ( ! parser.associations.has( node ) ) {
        parser.associations.set( node, {} );
      }
      parser.associations.get( node ).nodes = nodeIndex;

      for ( let i = 0, il = children.length; i < il; i ++ ) {

        node.push( children[ i ] );

      }

      return node;

    } );

  }

  /**
   * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
   */
  loadScene( sceneIndex ) {
    const sceneDef = this.json.scenes[ sceneIndex ];
    const parser = this;

    // Loader returns Group, not Scene.
    // See: https://github.com/mrdoob/three.js/issues/18342#issuecomment-578981172
    let scene;
    const nodeIds = sceneDef.nodes || [];

    const pending = [];

    for ( let i = 0, il = nodeIds.length; i < il; i ++ ) {

      pending.push( parser.getDependency( "node", nodeIds[ i ] ) );

    }

    return Promise.all( pending ).then( function ( nodes ) {
      scene = nodes;
      return scene;
    } );

  }

}

function GLTFRegistry() {

  let objects = {};

  return	{

    get: function ( key ) {

      return objects[ key ];

    },

    add: function ( key, object ) {

      objects[ key ] = object;

    },

    remove: function ( key ) {

      delete objects[ key ];

    },

    removeAll: function () {

      objects = {};

    }

  };

}

function createPrimitiveKey( primitiveDef ) {

  const dracoExtension = primitiveDef.extensions && primitiveDef.extensions[ EXTENSIONS.KHR_DRACO_MESH_COMPRESSION ];
  let geometryKey;

  if ( dracoExtension ) {

    geometryKey = "draco:" + dracoExtension.bufferView
      + ":" + dracoExtension.indices
      + ":" + createAttributesKey( dracoExtension.attributes );

  } else {

    geometryKey = primitiveDef.indices + ":" + createAttributesKey( primitiveDef.attributes ) + ":" + primitiveDef.mode;

  }

  return geometryKey;

}

function createAttributesKey( attributes ) {

  let attributesKey = "";

  const keys = Object.keys( attributes ).sort();

  for ( let i = 0, il = keys.length; i < il; i ++ ) {

    attributesKey += keys[ i ] + ":" + attributes[ keys[ i ] ] + ";";

  }

  return attributesKey;

}

function addPrimitiveAttributes( primitiveDef, parser ) {
  const geometry = {};

  const attributes = primitiveDef.attributes;

  const pending = [];

  function assignAttributeAccessor( accessorIndex, attributeName ) {

    return parser.getDependency( "accessor", accessorIndex )
      .then( function ( accessor ) {
        geometry[attributeName] = accessor;
      } );

  }

  for ( const gltfAttributeName in attributes ) {

    const geometryAttributeName = ATTRIBUTES[ gltfAttributeName ];

    // Skip attributes already provided by e.g. Draco extension.
    if ( !geometryAttributeName || geometryAttributeName in geometry ) continue;

    pending.push( assignAttributeAccessor( attributes[ gltfAttributeName ], geometryAttributeName ) );

  }

  if ( primitiveDef.indices !== undefined && ! geometry.indices ) {

    const accessor = parser.getDependency( "accessor", primitiveDef.indices ).then( function ( accessor ) {

      geometry.indices = accessor;

    } );

    pending.push( accessor );

  }

  return Promise.all( pending ).then( function () {
    return geometry;
  } );

}

function toTrianglesDrawMode( geometry, drawMode ) {

  if ( drawMode === TrianglesDrawMode ) {
    return geometry;
  }

  if ( drawMode === TriangleFanDrawMode || drawMode === TriangleStripDrawMode ) {

    let index = geometry.indices;

    // generate index if not present

    if ( index === null ) {

      const indices = [];

      const position = geometry.positions;

      if ( position !== undefined ) {

        for ( let i = 0; i < position.count; i ++ ) {

          indices.push( i );

        }

        geometry.indices = indices;
        index = geometry.indices;

      } else {

        throw new Error( "ExternalLoader.GLTFLoader.toTrianglesDrawMode(): Undefined position attribute. Processing not possible." );

      }

    }

    //

    const numberOfTriangles = index.count - 2;
    const newIndices = [];

    if ( drawMode === TriangleFanDrawMode ) {

      // gl.TRIANGLE_FAN

      for ( let i = 1; i <= numberOfTriangles; i ++ ) {

        newIndices.push( index[ 0 ] );
        newIndices.push( index[ i ] );
        newIndices.push( index[ i + 1 ] );

      }

    } else {

      // gl.TRIANGLE_STRIP

      for ( let i = 0; i < numberOfTriangles; i ++ ) {

        if ( i % 2 === 0 ) {

          newIndices.push( index[ i ] );
          newIndices.push( index[ i + 1 ] );
          newIndices.push( index[ i + 2 ] );

        } else {

          newIndices.push( index[ i + 2 ] );
          newIndices.push( index[ i + 1 ] );
          newIndices.push( index[ i ] );

        }

      }

    }

    if ( ( newIndices.length / 3 ) !== numberOfTriangles ) {

      throw new Error( "ExternalLoader.GLTFLoader.toTrianglesDrawMode(): Unable to generate correct amount of triangles." );

    }

    // build final geometry

    const newGeometry = new GeometryRawData(geometry.positions);
    newGeometry.indices = newIndices;
    newGeometry.uvs = geometry.uvs;
    newGeometry.normals = geometry.normals;

    return newGeometry;

  } else {

    throw new Error( "ExternalLoader.GLTFLoader.toTrianglesDrawMode(): Unknown draw mode:", drawMode );

  }

}

class GLTFBinaryExtension {

  constructor( data ) {

    this.name = EXTENSIONS.KHR_BINARY_GLTF;
    this.content = null;
    this.body = null;

    const headerView = new DataView( data, 0, BINARY_EXTENSION_HEADER_LENGTH );
    this.header = {
      magic: decodeText( new Uint8Array( data.slice( 0, 4 ) ) ),
      version: headerView.getUint32( 4, true ),
      length: headerView.getUint32( 8, true )
    };

    if ( this.header.magic !== BINARY_EXTENSION_HEADER_MAGIC ) {

      throw new Error( "ExternalLoader.GLTFLoader: Unsupported glTF-Binary header." );

    } else if ( this.header.version < 2.0 ) {

      throw new Error( "ExternalLoader.GLTFLoader: Legacy binary file detected." );

    }

    const chunkContentsLength = this.header.length - BINARY_EXTENSION_HEADER_LENGTH;
    const chunkView = new DataView( data, BINARY_EXTENSION_HEADER_LENGTH );
    let chunkIndex = 0;

    while ( chunkIndex < chunkContentsLength ) {

      const chunkLength = chunkView.getUint32( chunkIndex, true );
      chunkIndex += 4;

      const chunkType = chunkView.getUint32( chunkIndex, true );
      chunkIndex += 4;

      if ( chunkType === BINARY_EXTENSION_CHUNK_TYPES.JSON ) {

        const contentArray = new Uint8Array( data, BINARY_EXTENSION_HEADER_LENGTH + chunkIndex, chunkLength );
        this.content = decodeText( contentArray );

      } else if ( chunkType === BINARY_EXTENSION_CHUNK_TYPES.BIN ) {

        const byteOffset = BINARY_EXTENSION_HEADER_LENGTH + chunkIndex;
        this.body = data.slice( byteOffset, byteOffset + chunkLength );

      }

      // Clients must ignore chunks with unknown types.

      chunkIndex += chunkLength;

    }

    if ( this.content === null ) {

      throw new Error( "ExternalLoader.GLTFLoader: JSON content not found." );

    }

  }

}
